/**
 * Screener + Feedback i18n.
 *
 * Lightweight, dictionary-based localization for the pre-screener and
 * post-study survey — the two places where a non-English Prolific tester
 * is most likely to feel locked out. Anything not in the dictionary
 * gracefully falls back to English.
 *
 * Language is auto-derived from the signed-in user's profile.country (via
 * SUPPORTED_COUNTRIES.defaultLang) but the user can manually override
 * with the language picker at the top of /screener or /feedback. The
 * choice persists in localStorage so coming back to a different page in
 * the same language keeps it sticky.
 *
 * To add a new language:
 *   1. Add the locale code to LANG_OPTIONS below.
 *   2. Add a SCREENER + FEEDBACK dict for that locale.
 *   3. (Optional) Add the country to /lib/countries.js so the picker
 *      auto-detects it.
 */

import { useEffect, useMemo, useState } from "react";

export const LANG_OPTIONS = [
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "fr", label: "Français" },
    { code: "de", label: "Deutsch" },
];

const LS_KEY = "ml_locale_v1";

function detectLang(user) {
    if (typeof window !== "undefined") {
        try {
            const saved = window.localStorage.getItem(LS_KEY);
            if (saved && LANG_OPTIONS.some((l) => l.code === saved)) return saved;
        } catch { /* ignore */ }
    }
    // Map profile.country → defaultLang via a small inline lookup so this
    // module has no circular import.
    const c = (user?.profile?.country || "").toUpperCase();
    if (c === "MX" || c === "ES") return "es";
    if (c === "FR") return "fr";
    if (c === "DE") return "de";
    return "en";
}

export function useLocale(user) {
    const [lang, setLangState] = useState(() => detectLang(user));
    // Re-derive when the user prop changes (e.g. after profile.country
    // is set by the screener's qualify branch).
    useEffect(() => {
        const detected = detectLang(user);
        // Only auto-update if the user hasn't manually picked a language.
        if (typeof window === "undefined") return;
        try {
            const saved = window.localStorage.getItem(LS_KEY);
            if (!saved) setLangState(detected);
        } catch { /* ignore */ }
    }, [user]);
    const setLang = (next) => {
        setLangState(next);
        if (typeof window === "undefined") return;
        try { window.localStorage.setItem(LS_KEY, next); } catch { /* ignore */ }
    };
    return { lang, setLang };
}

// ============================================================================
// SCREENER STRINGS — every question text, sub-copy, and option label has a
// translation in each supported language. Keys mirror the question + option
// values in /lib/screener.js so a quick `t.screener.questions.relationship`
// lookup gets you the right string.
// ============================================================================

const SCREENER_EN = {
    intro_title: "Quick check before we start",
    intro_body: "Five questions, about a minute. We're testing Memory Lane with a specific group of caregivers so the feedback we get is useful — your answers tell us if you're part of that group.",
    submit: "Continue",
    submit_busy: "Checking…",
    qualified: "Thanks — you qualified. Starting the study now.",
    not_qualified_title: "Thank you — this study isn't for you today.",
    not_qualified_body: "We're focused on family caregivers actively caring for someone with dementia. We appreciate your time.",
    questions: {
        cared_for_dementia: { text: "Are you currently caring for, or have you cared for, someone with dementia or Alzheimer's?", sub: "Either now or in the past — both count.", options: { yes: "Yes", no: "No" } },
        relationship: { text: "What's your relationship to the person you care for?", sub: "Memory Lane is built for family caregivers right now.", options: { parent: "Parent", spouse: "Spouse or partner", sibling: "Sibling", child: "My child", grandparent: "Grandparent", other_family: "Other family member", friend: "Friend / neighbor", paid: "Paid caregiver (CNA, home health, etc.)", none: "I'm not caring for anyone right now" } },
        frequency: { text: "How often are you providing care right now?", sub: "", options: { daily: "Daily", several_week: "Several times a week", weekly: "About once a week", monthly: "A few times a month", less: "Less than monthly" } },
        country: { text: "Where are you located?", sub: "Memory Lane currently ships country-specific guides for the countries below — pick whichever is closest to where you actually live and care.", options: {} /* country labels come from countries.js */ },
        age: { text: "What's your age?", sub: "", options: { u25: "Under 25", "25_39": "25–39", "40_59": "40–59", "60p": "60+" } },
    },
    outside_supported_label: "Somewhere else",
};

const SCREENER_ES = {
    intro_title: "Una revisión rápida antes de empezar",
    intro_body: "Cinco preguntas, aproximadamente un minuto. Estamos probando Memory Lane con un grupo específico de cuidadores para que los comentarios que recibamos sean útiles — tus respuestas nos dicen si formas parte de ese grupo.",
    submit: "Continuar",
    submit_busy: "Verificando…",
    qualified: "Gracias — calificas. Comenzando el estudio ahora.",
    not_qualified_title: "Gracias — este estudio no es para ti hoy.",
    not_qualified_body: "Nos enfocamos en cuidadores familiares que cuidan activamente a alguien con demencia. Agradecemos tu tiempo.",
    questions: {
        cared_for_dementia: { text: "¿Estás cuidando actualmente, o has cuidado, a alguien con demencia o Alzheimer?", sub: "Ya sea ahora o en el pasado — ambos cuentan.", options: { yes: "Sí", no: "No" } },
        relationship: { text: "¿Cuál es tu relación con la persona que cuidas?", sub: "Memory Lane está diseñado para cuidadores familiares en este momento.", options: { parent: "Padre/Madre", spouse: "Cónyuge o pareja", sibling: "Hermano/Hermana", child: "Mi hijo/hija", grandparent: "Abuelo/Abuela", other_family: "Otro miembro de la familia", friend: "Amigo / vecino", paid: "Cuidador pagado (CNA, salud en el hogar, etc.)", none: "No estoy cuidando a nadie en este momento" } },
        frequency: { text: "¿Con qué frecuencia estás brindando cuidado en este momento?", sub: "", options: { daily: "Diariamente", several_week: "Varias veces por semana", weekly: "Aproximadamente una vez por semana", monthly: "Algunas veces al mes", less: "Menos de una vez al mes" } },
        country: { text: "¿Dónde te encuentras?", sub: "Memory Lane actualmente ofrece guías específicas para los países a continuación — elige el que sea más cercano a donde vives y cuidas.", options: {} },
        age: { text: "¿Cuál es tu edad?", sub: "", options: { u25: "Menos de 25", "25_39": "25–39", "40_59": "40–59", "60p": "60+" } },
    },
    outside_supported_label: "Otro lugar",
};

const SCREENER_FR = {
    intro_title: "Vérification rapide avant de commencer",
    intro_body: "Cinq questions, environ une minute. Nous testons Memory Lane avec un groupe spécifique d'aidants familiaux pour que les retours que nous recevons soient utiles — vos réponses nous diront si vous en faites partie.",
    submit: "Continuer",
    submit_busy: "Vérification…",
    qualified: "Merci — vous êtes qualifié(e). L'étude commence maintenant.",
    not_qualified_title: "Merci — cette étude n'est pas pour vous aujourd'hui.",
    not_qualified_body: "Nous nous concentrons sur les aidants familiaux qui s'occupent activement d'une personne atteinte de démence. Nous apprécions votre temps.",
    questions: {
        cared_for_dementia: { text: "Vous occupez-vous actuellement, ou vous êtes-vous occupé(e), d'une personne atteinte de démence ou de la maladie d'Alzheimer ?", sub: "Que ce soit maintenant ou dans le passé — les deux comptent.", options: { yes: "Oui", no: "Non" } },
        relationship: { text: "Quel est votre lien avec la personne dont vous vous occupez ?", sub: "Memory Lane est conçu pour les aidants familiaux pour le moment.", options: { parent: "Parent", spouse: "Conjoint ou partenaire", sibling: "Frère ou sœur", child: "Mon enfant", grandparent: "Grand-parent", other_family: "Autre membre de la famille", friend: "Ami / voisin", paid: "Aidant rémunéré (auxiliaire de vie, soins à domicile, etc.)", none: "Je ne m'occupe de personne actuellement" } },
        frequency: { text: "À quelle fréquence prodiguez-vous des soins en ce moment ?", sub: "", options: { daily: "Quotidiennement", several_week: "Plusieurs fois par semaine", weekly: "Environ une fois par semaine", monthly: "Quelques fois par mois", less: "Moins d'une fois par mois" } },
        country: { text: "Où êtes-vous situé(e) ?", sub: "Memory Lane propose actuellement des guides spécifiques pour les pays ci-dessous — choisissez celui qui est le plus proche de votre lieu de vie réel.", options: {} },
        age: { text: "Quel est votre âge ?", sub: "", options: { u25: "Moins de 25", "25_39": "25–39", "40_59": "40–59", "60p": "60+" } },
    },
    outside_supported_label: "Ailleurs",
};

const SCREENER_DE = {
    intro_title: "Kurze Überprüfung, bevor wir beginnen",
    intro_body: "Fünf Fragen, etwa eine Minute. Wir testen Memory Lane mit einer bestimmten Gruppe von Pflegenden, damit die Rückmeldungen, die wir erhalten, nützlich sind — Ihre Antworten sagen uns, ob Sie zu dieser Gruppe gehören.",
    submit: "Weiter",
    submit_busy: "Wird geprüft…",
    qualified: "Danke — Sie qualifizieren sich. Die Studie beginnt jetzt.",
    not_qualified_title: "Danke — diese Studie ist heute nicht für Sie.",
    not_qualified_body: "Wir konzentrieren uns auf pflegende Angehörige, die sich aktiv um jemanden mit Demenz kümmern. Wir schätzen Ihre Zeit.",
    questions: {
        cared_for_dementia: { text: "Pflegen Sie derzeit jemanden mit Demenz oder Alzheimer, oder haben Sie das getan?", sub: "Sowohl jetzt als auch in der Vergangenheit — beides zählt.", options: { yes: "Ja", no: "Nein" } },
        relationship: { text: "Wie ist Ihre Beziehung zu der Person, die Sie pflegen?", sub: "Memory Lane ist im Moment für pflegende Angehörige konzipiert.", options: { parent: "Elternteil", spouse: "Ehepartner/in oder Partner/in", sibling: "Geschwister", child: "Mein Kind", grandparent: "Großelternteil", other_family: "Anderes Familienmitglied", friend: "Freund/Nachbar", paid: "Bezahlte Pflegekraft (Altenpfleger, häusliche Pflege usw.)", none: "Ich pflege derzeit niemanden" } },
        frequency: { text: "Wie oft pflegen Sie zurzeit?", sub: "", options: { daily: "Täglich", several_week: "Mehrmals pro Woche", weekly: "Etwa einmal pro Woche", monthly: "Ein paar Mal im Monat", less: "Weniger als monatlich" } },
        country: { text: "Wo befinden Sie sich?", sub: "Memory Lane bietet derzeit länderspezifische Leitfäden für die unten aufgeführten Länder — wählen Sie das Land, das Ihrem tatsächlichen Wohn- und Pflegeort am nächsten kommt.", options: {} },
        age: { text: "Wie alt sind Sie?", sub: "", options: { u25: "Unter 25", "25_39": "25–39", "40_59": "40–59", "60p": "60+" } },
    },
    outside_supported_label: "Anderswo",
};

const SCREENER_DICTS = { en: SCREENER_EN, es: SCREENER_ES, fr: SCREENER_FR, de: SCREENER_DE };

export function tScreener(lang) {
    return SCREENER_DICTS[lang] || SCREENER_EN;
}

// ============================================================================
// FEEDBACK STRINGS — same shape as screener. Keep keys flat for cheap lookups.
// ============================================================================

const FEEDBACK_EN = {
    intro_title: "Quick caregiver pulse",
    intro_body: "Five minutes. Your answers help us decide what to keep, fix, or remove. Skip anything that doesn't apply.",
    rating_label: "Overall, how would you rate Memory Lane so far?",
    submit: "Send feedback",
    submit_busy: "Sending…",
    retry: "Retry",
    error_intro: "We couldn't save your feedback yet.",
    error_holding: "Your answers are still on this page — nothing is lost.",
    sections: {
        use_again: "Would you use Memory Lane in your day-to-day caregiving?",
        helpful: "What felt most helpful?",
        confusing: "Anything that felt confusing, unnecessary, or missing?",
        feelings: "How did it make you feel?",
        miss_most: "If Memory Lane disappeared tomorrow, what would you miss most?",
        pmf: "How would you feel if you could no longer use Memory Lane?",
        return_freq: "How often do you think you'd come back to it?",
        wom: "On a scale of 0–10, how likely are you to recommend Memory Lane to a fellow caregiver?",
        wom_first_person: "Who's the first caregiver you'd tell about it? (First name + your relationship is plenty.)",
        contact: "Email (optional — if you'd like us to follow up)",
    },
    use_again: { yes: "Yes, definitely", maybe: "Maybe", no: "Probably not" },
    pmf: { very: "Very disappointed", somewhat: "Somewhat disappointed", not: "Not disappointed", na: "I'd find another way" },
    return_freq: { daily: "Daily", weekly: "Weekly", monthly: "Monthly", rarely: "Rarely", never: "Never" },
    feelings_chips: { relieved: "Relieved", less_alone: "Less alone", hopeful: "Hopeful", overwhelmed: "Overwhelmed", confused: "Confused", same: "About the same" },
};

const FEEDBACK_ES = {
    intro_title: "Tu opinión como cuidador",
    intro_body: "Cinco minutos. Tus respuestas nos ayudan a decidir qué mantener, arreglar o quitar. Omite lo que no te aplique.",
    rating_label: "En general, ¿cómo calificarías a Memory Lane hasta ahora?",
    submit: "Enviar comentarios",
    submit_busy: "Enviando…",
    retry: "Reintentar",
    error_intro: "No pudimos guardar tu opinión aún.",
    error_holding: "Tus respuestas siguen aquí — nada se ha perdido.",
    sections: {
        use_again: "¿Usarías Memory Lane en tu cuidado del día a día?",
        helpful: "¿Qué te resultó más útil?",
        confusing: "¿Algo que te resultó confuso, innecesario o que faltaba?",
        feelings: "¿Cómo te hizo sentir?",
        miss_most: "Si Memory Lane desapareciera mañana, ¿qué extrañarías más?",
        pmf: "¿Cómo te sentirías si ya no pudieras usar Memory Lane?",
        return_freq: "¿Con qué frecuencia crees que volverías a usarlo?",
        wom: "En una escala de 0 a 10, ¿qué tan probable es que recomiendes Memory Lane a otro cuidador?",
        wom_first_person: "¿Quién es el primer cuidador a quien le contarías? (Primer nombre + tu relación es suficiente.)",
        contact: "Correo electrónico (opcional — si te gustaría que te contactemos)",
    },
    use_again: { yes: "Sí, definitivamente", maybe: "Quizás", no: "Probablemente no" },
    pmf: { very: "Muy decepcionado/a", somewhat: "Algo decepcionado/a", not: "No decepcionado/a", na: "Encontraría otra manera" },
    return_freq: { daily: "Diariamente", weekly: "Semanalmente", monthly: "Mensualmente", rarely: "Raramente", never: "Nunca" },
    feelings_chips: { relieved: "Aliviado/a", less_alone: "Menos solo/a", hopeful: "Esperanzado/a", overwhelmed: "Abrumado/a", confused: "Confundido/a", same: "Más o menos igual" },
};

const FEEDBACK_FR = {
    intro_title: "Vos impressions d'aidant",
    intro_body: "Cinq minutes. Vos réponses nous aident à décider quoi garder, corriger ou supprimer. Sautez ce qui ne s'applique pas.",
    rating_label: "Globalement, comment évalueriez-vous Memory Lane jusqu'à présent ?",
    submit: "Envoyer les commentaires",
    submit_busy: "Envoi…",
    retry: "Réessayer",
    error_intro: "Nous n'avons pas pu enregistrer vos commentaires.",
    error_holding: "Vos réponses sont toujours sur cette page — rien n'est perdu.",
    sections: {
        use_again: "Utiliseriez-vous Memory Lane dans votre quotidien d'aidant ?",
        helpful: "Qu'est-ce qui vous a semblé le plus utile ?",
        confusing: "Y a-t-il quelque chose qui vous a semblé confus, inutile ou manquant ?",
        feelings: "Comment cela vous a-t-il fait sentir ?",
        miss_most: "Si Memory Lane disparaissait demain, qu'est-ce qui vous manquerait le plus ?",
        pmf: "Comment vous sentiriez-vous si vous ne pouviez plus utiliser Memory Lane ?",
        return_freq: "À quelle fréquence pensez-vous y revenir ?",
        wom: "Sur une échelle de 0 à 10, à quel point êtes-vous susceptible de recommander Memory Lane à un autre aidant ?",
        wom_first_person: "À quel aidant en parleriez-vous en premier ? (Prénom + votre lien suffit.)",
        contact: "E-mail (facultatif — si vous souhaitez un suivi)",
    },
    use_again: { yes: "Oui, certainement", maybe: "Peut-être", no: "Probablement pas" },
    pmf: { very: "Très déçu(e)", somewhat: "Plutôt déçu(e)", not: "Pas déçu(e)", na: "Je trouverais un autre moyen" },
    return_freq: { daily: "Quotidiennement", weekly: "Hebdomadairement", monthly: "Mensuellement", rarely: "Rarement", never: "Jamais" },
    feelings_chips: { relieved: "Soulagé(e)", less_alone: "Moins seul(e)", hopeful: "Plein(e) d'espoir", overwhelmed: "Submergé(e)", confused: "Confus(e)", same: "À peu près pareil" },
};

const FEEDBACK_DE = {
    intro_title: "Schnelle Pflegeperson-Umfrage",
    intro_body: "Fünf Minuten. Ihre Antworten helfen uns zu entscheiden, was wir beibehalten, korrigieren oder entfernen sollen. Überspringen Sie, was nicht zutrifft.",
    rating_label: "Wie würden Sie Memory Lane insgesamt bewerten?",
    submit: "Feedback senden",
    submit_busy: "Wird gesendet…",
    retry: "Erneut versuchen",
    error_intro: "Wir konnten Ihr Feedback noch nicht speichern.",
    error_holding: "Ihre Antworten sind noch auf dieser Seite — nichts ist verloren.",
    sections: {
        use_again: "Würden Sie Memory Lane in Ihrer täglichen Pflege verwenden?",
        helpful: "Was fanden Sie am hilfreichsten?",
        confusing: "Gibt es etwas, das verwirrend, unnötig oder fehlend war?",
        feelings: "Wie hat es Sie fühlen lassen?",
        miss_most: "Wenn Memory Lane morgen verschwinden würde, was würden Sie am meisten vermissen?",
        pmf: "Wie würden Sie sich fühlen, wenn Sie Memory Lane nicht mehr nutzen könnten?",
        return_freq: "Wie oft würden Sie es wahrscheinlich wiederverwenden?",
        wom: "Auf einer Skala von 0 bis 10, wie wahrscheinlich ist es, dass Sie Memory Lane einer anderen Pflegeperson empfehlen?",
        wom_first_person: "Wer ist die erste Pflegeperson, der Sie davon erzählen würden? (Vorname + Ihre Beziehung reicht.)",
        contact: "E-Mail (optional — falls Sie ein Follow-up wünschen)",
    },
    use_again: { yes: "Ja, definitiv", maybe: "Vielleicht", no: "Wahrscheinlich nicht" },
    pmf: { very: "Sehr enttäuscht", somewhat: "Etwas enttäuscht", not: "Nicht enttäuscht", na: "Ich würde einen anderen Weg finden" },
    return_freq: { daily: "Täglich", weekly: "Wöchentlich", monthly: "Monatlich", rarely: "Selten", never: "Nie" },
    feelings_chips: { relieved: "Erleichtert", less_alone: "Weniger allein", hopeful: "Hoffnungsvoll", overwhelmed: "Überfordert", confused: "Verwirrt", same: "Etwa gleich" },
};

const FEEDBACK_DICTS = { en: FEEDBACK_EN, es: FEEDBACK_ES, fr: FEEDBACK_FR, de: FEEDBACK_DE };

export function tFeedback(lang) {
    return FEEDBACK_DICTS[lang] || FEEDBACK_EN;
}

/**
 * Convenience: detect both dicts in one call so a page using both
 * (e.g. the screener-then-feedback flow) gets a single object.
 */
export function useStudyI18n(user) {
    const { lang, setLang } = useLocale(user);
    const dicts = useMemo(() => ({
        lang,
        screener: tScreener(lang),
        feedback: tFeedback(lang),
        options: LANG_OPTIONS,
        setLang,
    }), [lang, setLang]);
    return dicts;
}
